import React, { useEffect, useState, useCallback, useRef } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import AddFieldOnStep from '../../../../components/Pipelines/Modals/AddFieldOnStep'
import AddStepInPipeline from '../../../../components/Pipelines/Modals/AddStepInPipeline'
import ViewOrEditTicket from '../../../../components/Pipelines/Modals/ViewOrEditTicket'
import ViewOrEditTags from '../../../../components/Pipelines/Modals/ViewOrEditTags'
import FieldTypeSelector from '../../../../components/Pipelines/Modals/FieldTypeSelector'
import AddIntegratedFieldModal from '../../../../components/Pipelines/Modals/AddIntegratedFieldModal'
import TagComponent from '../../../../components/ui/Tag'
import { useDrag, useDrop } from 'react-dnd'
import { Step, Ticket } from '../types'
import { Pipeline, StepColumn, StepHeader, TicketItem, TicketList, CardTitle, MetaData, HorizontalDiv, AddInStep, InputForAdd, AddStep, TitleWithActions, ActionGroup, IconButton } from '../style'
import { CircleAlertIcon, MessageSquareText, Plus, User, X, SquarePen, Trash2, Check } from 'lucide-react'
import axios from '../../../../config/axios'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePipelineContext } from '../PipelineContext'

type DragItem = { id: number; stepId: number; index: number }
type TicketOrderCache = Record<number, Ticket[]>

const DraggableTicket = ({
  ticket,
  index,
  onOpen,
  onEditClick,
  isEditing,
  titleDraft,
  onChangeTitle,
  onConfirmEdit,
  onCancelEdit,
  onDelete,
}: {
  ticket: Ticket
  index: number
  onOpen: () => void
  onEditClick: () => void
  isEditing: boolean
  titleDraft: string
  onChangeTitle: (value: string) => void
  onConfirmEdit: () => void
  onCancelEdit: () => void
  onDelete: () => void
}) => {
  const ref = useRef<HTMLLIElement | null>(null)

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean}>({
    type: 'ticket',
    item: { id: ticket.id, stepId: ticket.stepId, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(ref)

  return (
    <TicketItem
      ref={ref}
      onDoubleClick={onOpen}
      style={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move'
      }}
    >
      {ticket.urgency && (
        <HorizontalDiv>
          <MetaData color='#BF383B' fontSize='10px'><CircleAlertIcon size={16} /> URGENTE</MetaData>
        </HorizontalDiv>
      )}
      <TitleWithActions>
        {isEditing ? (
          <InputForAdd
            value={titleDraft}
            placeholder='Título do card'
            onChange={(e) => onChangeTitle(e.target.value)}
            onKeyDown={(e) => {
              if(e.key === 'Enter') onConfirmEdit()
              if(e.key === 'Escape') onCancelEdit()
            }}
            autoFocus
          />
        ) : (
          <CardTitle>{ticket.title}</CardTitle>
        )}
        {isEditing ? (
          <ActionGroup>
            <IconButton onClick={onConfirmEdit} onDoubleClick={(e) => e.stopPropagation()}><Check size={16} /></IconButton>
            <IconButton onClick={onCancelEdit} onDoubleClick={(e) => e.stopPropagation()}><X size={16} /></IconButton>
          </ActionGroup>
        ) : (
          <ActionGroup>
            <IconButton onClick={onEditClick} onDoubleClick={(e) => e.stopPropagation()}><SquarePen size={16} /></IconButton>
            <IconButton onClick={onDelete} onDoubleClick={(e) => e.stopPropagation()}><Trash2 size={16} /></IconButton>
          </ActionGroup>
        )}
      </TitleWithActions>
      {ticket.createBy && (
        <HorizontalDiv>
          <MetaData>
            <div style={{width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#0090D3', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              {ticket.createBy.picture ? <img src={ticket.createBy.picture} alt={ticket.createBy.name} style={{width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover'}} /> : <User size={20} />}
            </div>
            {ticket.createBy.name}
          </MetaData>
        </HorizontalDiv>
      )}
      {ticket.comments.length > 0 &&
        <HorizontalDiv>
          <MetaData><MessageSquareText color='#C4C4C4' size={16} /> {ticket.comments.length}</MetaData>
        </HorizontalDiv>
      }
      {ticket.tags.length > 0 &&
        <HorizontalDiv>
          {ticket.tags.map(tag => <TagComponent name={tag.tag.name} color={tag.tag.color} key={tag.tag.id} />)}
        </HorizontalDiv>
      }
    </TicketItem>
  )
}

const DroppableStepColumn = ({ step, children, onTicketMove, onStepMove }: { 
  step: Step, 
  children: React.ReactNode,
  onTicketMove: (ticketId: number, newStepId: number) => void,
  onStepMove: (stepId: number, targetStepId: number) => void
}) => {
  const ref = useRef<HTMLDivElement | null>(null)

  const [{ isOver }, drop] = useDrop({
    accept: ['ticket', 'step'],
    drop: (item: { id: number, stepId?: number }, monitor) => {
      const itemType = monitor.getItemType()
      if (itemType === 'ticket' && item.stepId !== step.id) {
        onTicketMove(item.id, step.id)
      }
      if (itemType === 'step' && item.id !== step.id) {
        onStepMove(item.id, step.id)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  const [{ isDraggingStep }, drag] = useDrag({
    type: 'step',
    item: { id: step.id },
    collect: (monitor) => ({
      isDraggingStep: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <StepColumn 
      ref={ref}
      style={{ 
        border: isOver ? '2px solid rgba(0, 144, 211, 0.3)' : 'none',
        opacity: isDraggingStep ? 0.6 : 1,
      }}
    >
      {children}
    </StepColumn>
  )
}

type FieldModalType = 
  | { mode: 'selector', stepId: number }
  | { mode: 'custom', stepId: number, field?: any }
  | { mode: 'integrated', stepId: number }
  | null

const Kanban = () => {
  const { isConfiguringPipeline, viewOrEditTagsModalIsOpen, setViewOrEditTagsModalIsOpen } = usePipelineContext()
  const [ fieldModal, setFieldModal ] = useState<FieldModalType>(null)
  const [ fieldsInSteps, setFieldsInSteps ] = useState<{pipelineField:any, step:any}[]>([])
  const [ addStepModalIsOpen, setAddStepModalIsOpen ] = useState<boolean>(false)
  const navigate = useNavigate()
  const [ steps, setSteps ] = useState<Step[]>([])
  const [ tickets, setTickets ] = useState<Ticket[]>([])
  const [ newTicketIn, setNewTicketIn ] = useState<number | null>(null)
  const [ newTicketTitle, setNewTicketTitle ] = useState<string>('')
  const [ creatingTicket, setCreatingTicket ] = useState<boolean>(false)
  const [ editingTicketId, setEditingTicketId ] = useState<number | null>(null)
  const [ ticketTitleDraft, setTicketTitleDraft ] = useState<string>('')
  const [ editingFieldId, setEditingFieldId ] = useState<number | null>(null)
  const [ fieldNameDraft, setFieldNameDraft ] = useState<string>('')
  const [ editingStepId, setEditingStepId ] = useState<number | null>(null)
  const [ stepNameDraft, setStepNameDraft ] = useState<string>('')
  const [ typeMap, setTypeMap ] = useState<Record<number, string>>({})
  const [ searchParams ] = useSearchParams()
  const ticketOrderRef = useRef<TicketOrderCache>({})
  const pipelineId = searchParams.get('pipelineId')

  const fetchFieldsInStepsData = useCallback(async () => {
    if(!pipelineId) return
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields?pipelineId=${pipelineId}`)
      if(response.data) {
        setFieldsInSteps(response.data.data)
      }
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  useEffect(() => {
    const fetchFieldTypes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/types`)
        if(response.data?.data) {
          const map: Record<number, string> = {}
          response.data.data.forEach((item: {id:number, name:string}) => { map[item.id] = item.name })
          setTypeMap(map)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchFieldTypes()
  }, [])

  const fetchStepsAndTickets = useCallback(async () => {
    if(!pipelineId) return
    try {
      const [responseSteps, responseTickets] = await Promise.all([
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`),
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket?pipelineId=${pipelineId}`)
      ])
      if(responseSteps.data.data) setSteps(responseSteps.data.data)
      if(responseTickets.data.data) setTickets(responseTickets.data.data)
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  const saveStepOrder = useCallback(async (orderedSteps: Step[]) => {
    if(!pipelineId) return
    try {
      await Promise.all(
        orderedSteps.map((step, idx) =>
          axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${step.id}` , {
            position: idx,
          })
        )
      )
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  const saveTicketOrder = useCallback(async (stepId: number, ticketsInStep: Ticket[]) => {
    try {
      await Promise.all(
        ticketsInStep.map((ticket) =>
          axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticket.id}`, {
            stepId,
          })
        )
      )
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if(!isConfiguringPipeline) {
      setEditingStepId(null)
      setStepNameDraft('')
    }

    if(isConfiguringPipeline) {
      fetchFieldsInStepsData()
    }
  }, [isConfiguringPipeline, fetchFieldsInStepsData])

  useEffect(() => {
    fetchStepsAndTickets()
  }, [fetchStepsAndTickets])

  useEffect(() => {
    if(!addStepModalIsOpen) {
      fetchStepsAndTickets()
    }
  }, [addStepModalIsOpen, fetchStepsAndTickets])

  const createTicket = async () => {
    if(creatingTicket) return
    const trimmed = newTicketTitle.trim()
    if(!trimmed || newTicketIn === null) {
      setNewTicketIn(null)
      setNewTicketTitle('')
      return
    }
    try {
      setCreatingTicket(true)
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: trimmed,
        stepId: newTicketIn
      })
      if(response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[]}])
      }
    } catch (error) {
      console.error(error)
    } finally {
      setCreatingTicket(false)
      setNewTicketIn(null)
      setNewTicketTitle('')
    }
  }

  const handleCreateTicket = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === 'Enter') {
      await createTicket()
    }
    if(e.key === 'Escape') {
      setNewTicketIn(null)
      setNewTicketTitle('')
    }
  }

  const handleUpdateStep = async (stepId: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === 'Enter') {
      await saveStepName(stepId)
    }
    if(e.key === 'Escape') {
      cancelEditStep()
    }
  }

  const saveStepName = async (stepId: number) => {
    const newName = stepNameDraft.trim()
    if(!newName) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`, {
        name: newName
      })
      if(response.data) {
        setSteps(prev => prev.map(step => step.id === stepId ? response.data.data : step))
        setEditingStepId(null)
        setStepNameDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditStep = () => {
    setEditingStepId(null)
    setStepNameDraft('')
  }

  const handleDeleteStep = async (stepId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`)
      setSteps(prev => prev.filter(step => step.id !== stepId))
      setTickets(prev => prev.filter(ticket => ticket.stepId !== stepId))
      setFieldsInSteps(prev => prev.filter(field => field.step.id !== stepId))
      if(editingStepId === stepId) {
        cancelEditStep()
      }
    } catch (error) {
      console.error(error)
    }
  }
  const handleDeleteField = async (stepId: number, fieldId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field/${fieldId}`)
      setFieldsInSteps(prev => prev.filter(field => !(field.pipelineField.id === fieldId && field.step.id === stepId)))
    } catch (error) {
      console.error(error)
    }
  }

  const moveStep = (sourceStepId: number, targetStepId: number) => {
    const sourceIndex = steps.findIndex(step => step.id === sourceStepId)
    const targetIndex = steps.findIndex(step => step.id === targetStepId)
    if(sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return
    const updated = [...steps]
    const [removed] = updated.splice(sourceIndex, 1)
    updated.splice(targetIndex, 0, removed)
    const positioned = updated.map((step, idx) => ({ ...step, position: idx }))
    setSteps(positioned)
    saveStepOrder(positioned)
  }


  const handleTicketMove = async (ticketId: number, newStepId: number) => {
    try {
      const filledResponse = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/filled`)
      let isFilled = true
      if(filledResponse.data) {
        filledResponse.data.map((field:any) => {
          if(field.every((field:any) => field === null)) {
            return isFilled = false
          }
        })
        if(!isFilled) return navigate(`/panel/pipeline/ticket?ticketId=${ticketId}&isFilled=false`)
      }
      const sourceStepId = tickets.find((t) => t.id === ticketId)?.stepId
      const targetTickets = tickets.filter((t) => t.stepId === newStepId && t.id !== ticketId)
      const newPosition = targetTickets.length
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        stepId: newStepId,
      })
      if(response.data) {
        setTickets(prev => {
          const moved = prev.find((t) => t.id === ticketId)
          const withoutMoved = prev.filter((t) => t.id !== ticketId)
          const targetList = withoutMoved.filter((t) => t.stepId === newStepId)
          const sourceList = sourceStepId ? withoutMoved.filter((t) => t.stepId === sourceStepId) : []
          const newTarget = [...targetList, { ...(moved || response.data.data), stepId: newStepId, position: newPosition }]
          const reorderedTarget = newTarget.map((t, idx) => ({ ...t, position: idx }))
          const reorderedSource = sourceStepId && sourceStepId !== newStepId
            ? sourceList.map((t, idx) => ({ ...t, position: idx }))
            : sourceList
          const others = withoutMoved.filter((t) => t.stepId !== newStepId && (!sourceStepId || t.stepId !== sourceStepId))
          if (sourceStepId && sourceStepId !== newStepId && reorderedSource.length) {
            saveTicketOrder(sourceStepId, reorderedSource)
          }
          saveTicketOrder(newStepId, reorderedTarget)
          return [...others, ...reorderedSource, ...reorderedTarget]
        })
      }
    } catch (error) {
      console.error('Erro ao mover ticket:', error)
    }
  }

  const saveTicketTitle = async (ticketId: number) => {
    const newTitle = ticketTitleDraft.trim()
    if(!newTitle) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        title: newTitle
      })
      if(response.data) {
        setTickets(prev => prev.map(ticket => ticket.id === ticketId ? {...ticket, title: response.data.data.title} : ticket))
        setEditingTicketId(null)
        setTicketTitleDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditTicket = () => {
    setEditingTicketId(null)
    setTicketTitleDraft('')
  }

  const saveFieldName = async (fieldId: number) => {
    const trimmed = fieldNameDraft.trim()
    if(!trimmed) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${fieldId}`, {
        name: trimmed
      })
      if(response.data) {
        setFieldsInSteps((prev) => prev.map((item) => item.pipelineField.id === fieldId ? { ...item, pipelineField: { ...item.pipelineField, name: response.data.data.name } } : item))
        setEditingFieldId(null)
        setFieldNameDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditField = () => {
    setEditingFieldId(null)
    setFieldNameDraft('')
  }

  const handleDeleteTicket = async (ticketId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`)
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId))
      if(editingTicketId === ticketId) {
        cancelEditTicket()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Pipeline>
        {steps.map(step => (
          <DroppableStepColumn 
            key={step.id} 
            step={step} 
            onTicketMove={handleTicketMove}
            onStepMove={moveStep}
          >
            <StepHeader>
              <TitleWithActions>
                {editingStepId === step.id ? (
                  <InputForAdd
                    value={stepNameDraft}
                    placeholder='Nome da etapa'
                    onChange={(e) => setStepNameDraft(e.target.value)}
                    onKeyDown={(e) => handleUpdateStep(step.id, e)}
                    autoFocus
                  />
                ) : (
                  <CardTitle>{step.name}</CardTitle>
                )}
                {editingStepId === step.id ? (
                  <ActionGroup>
                    <IconButton onClick={() => saveStepName(step.id)}><Check size={16} /></IconButton>
                    <IconButton onClick={cancelEditStep}><X size={16} /></IconButton>
                  </ActionGroup>
                ) : (
                  <ActionGroup>
                    <IconButton onClick={() => {
                      setEditingStepId(step.id)
                      setStepNameDraft(step.name)
                    }}><SquarePen size={16} /></IconButton>
                    <IconButton onClick={() => handleDeleteStep(step.id)}><Trash2 size={16} /></IconButton>
                  </ActionGroup>
                )}
              </TitleWithActions>
            </StepHeader>
            <TicketList>
              {isConfiguringPipeline ? <>
                <AddInStep onClick={() => setFieldModal({ mode: 'selector', stepId: step.id })}><Plus />Adicionar campo</AddInStep>
                {fieldsInSteps.filter((field) => field.step.id === step.id).map((field) => {
                  const isEditingField = editingFieldId === field.pipelineField.id
                  const isIntegrated = !!field.pipelineField.integrationConfig
                  const typeId = field.pipelineField.typeField?.id || (field.pipelineField as any).typeFieldId
                  const typeLabel = isIntegrated ? 'Integrado' : (field.pipelineField.typeField?.name || (typeId ? typeMap[typeId] : '') || '')
                  return (
                    <TicketItem key={field.pipelineField.id}>
                      <TitleWithActions>
                        {isEditingField ? (
                          <InputForAdd
                            value={fieldNameDraft}
                            placeholder='Nome do campo'
                            onChange={(e) => setFieldNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if(e.key === 'Enter') saveFieldName(field.pipelineField.id)
                              if(e.key === 'Escape') cancelEditField()
                            }}
                            autoFocus
                          />
                        ) : (
                          <CardTitle>
                            {field.pipelineField.name} {typeLabel ? <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>({typeLabel})</span> : null}
                          </CardTitle>
                        )}
                        <ActionGroup>
                          {isEditingField ? (
                            <>
                              <IconButton onClick={() => saveFieldName(field.pipelineField.id)} onDoubleClick={(e) => e.stopPropagation()}><Check size={16} /></IconButton>
                              <IconButton onClick={cancelEditField} onDoubleClick={(e) => e.stopPropagation()}><X size={16} /></IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton onClick={() => {
                                setEditingFieldId(field.pipelineField.id)
                                setFieldNameDraft(field.pipelineField.name)
                              }} onDoubleClick={(e) => e.stopPropagation()}><SquarePen size={16} /></IconButton>
                              <IconButton onClick={() => handleDeleteField(step.id, field.pipelineField.id)} onDoubleClick={(e) => e.stopPropagation()}><Trash2 size={16} /></IconButton>
                            </>
                          )}
                        </ActionGroup>
                      </TitleWithActions>
                    </TicketItem>
                  )
                })}
              </> 
              : <>
                {tickets
                  .filter((ticket) => ticket.stepId === step.id)
                  .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id)
                  .map((ticket, idx) => 
                  <DraggableTicket 
                    key={ticket.id} 
                    ticket={ticket} 
                    index={idx}
                    onOpen={() => navigate(`/panel/pipeline/ticket?ticketId=${ticket.id}`)}
                    onEditClick={() => {
                      setEditingTicketId(ticket.id)
                      setTicketTitleDraft(ticket.title)
                    }}
                    isEditing={editingTicketId === ticket.id}
                    titleDraft={ticketTitleDraft}
                    onChangeTitle={setTicketTitleDraft}
                    onConfirmEdit={() => saveTicketTitle(ticket.id)}
                    onCancelEdit={cancelEditTicket}
                    onDelete={() => handleDeleteTicket(ticket.id)}
                  />
                )}
              </>}
              {newTicketIn === step.id && (
                <TicketItem>
                  <HorizontalDiv style={{backgroundColor: '#F4F4F4', paddingRight: '1rem'}}>
                    <InputForAdd 
                      value={newTicketTitle} 
                      onChange={(e) => setNewTicketTitle(e.target.value)} 
                      placeholder='Título do card' 
                      onKeyDown={handleCreateTicket}
                      autoFocus
                      onBlur={createTicket}
                    /> <X color='#696969' size={20} style={{cursor: 'pointer'}} onClick={() => setNewTicketIn(null)} onDoubleClick={(e) => e.stopPropagation()} />
                  </HorizontalDiv>
                </TicketItem>
              )}
              {!isConfiguringPipeline && <AddInStep onClick={() => setNewTicketIn(step.id)}><Plus />Adicionar card</AddInStep>}
            </TicketList>
          </DroppableStepColumn>
        ))}
        <StepColumn><AddStep onClick={() => setAddStepModalIsOpen(true)}><Plus strokeWidth={1.5} /></AddStep></StepColumn>
      </Pipeline>
      {isConfiguringPipeline && fieldModal?.mode === 'selector' && (
        <FieldTypeSelector
          closeModal={() => setFieldModal(null)}
          onSelectCustomField={() => setFieldModal({ mode: 'custom', stepId: fieldModal.stepId })}
          onSelectIntegratedField={() => setFieldModal({ mode: 'integrated', stepId: fieldModal.stepId })}
        />
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'custom' && (
        <AddFieldOnStep
          stepId={fieldModal.stepId}
          editingField={fieldModal.field}
          closeModal={() => {
            setFieldModal(null)
            fetchFieldsInStepsData()
          }}
          onBack={() => setFieldModal({ mode: 'selector', stepId: fieldModal.stepId })}
          onSaved={(updated) => {
            setFieldModal(null)
            if (updated) {
              setFieldsInSteps((prev) => prev.map((item) => item.pipelineField.id === updated.id ? { ...item, pipelineField: { ...item.pipelineField, name: updated.name } } : item))
            }
            fetchFieldsInStepsData()
          }}
        />
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'integrated' && (
        <AddIntegratedFieldModal
          stepId={fieldModal.stepId}
          closeModal={() => setFieldModal(null)}
          onBack={() => setFieldModal({ mode: 'selector', stepId: fieldModal.stepId })}
          onSaved={() => {
            setFieldModal(null)
            fetchFieldsInStepsData()
          }}
        />
      )}
      {addStepModalIsOpen && <AddStepInPipeline pipelineId={Number(pipelineId)} closeModal={() => setAddStepModalIsOpen(false)} />}
      {viewOrEditTagsModalIsOpen && <ViewOrEditTags closeModal={() => setViewOrEditTagsModalIsOpen(false)} />}
    </DndProvider>
  )
}

export default Kanban